import type {
    BIEvt,
    BaseLogger,
    DSConfig,
    Experiment,
    Pointer,
    Pointers,
    Transaction,
    EventEmitter,
    PageListWithMasterAndMainPage,
    PageList,
    FragmentListItem
} from '@wix/document-services-types'
export type {Transaction, HistoryItem, Key, Namespace, EventEmitter} from '@wix/document-services-types'
import type {CustomGetter, DalValueChangeCallback, DAL} from './dal/documentManagerDal'
import type {ValueToIndexIds} from './dal/queryIndex/queryIndex'
import type {DalValue, DmStore} from './dal/store'
import type {CoreConfig, ModelsInitializationType} from './documentManagerCore'
import type {ValidateValue, ValidatorResult} from './dal/validation/dalValidation'
import type {ReportableError} from '@wix/document-manager-utils'
import type {ResourceLoaderAPI, ResourceLoaderDefinition} from './resourceLoader'

export type CreateExtension = (extensionArgs: CreateExtensionArgument) => Extension

export type Null<T> = T | null

export type NullUndefined<T> = Null<T> | undefined

export interface TransactionQueue {
    run(transaction: Transaction[]): void
}

export type KeyValPredicate = (v: any, k: string) => boolean

export interface IndexKey {
    verificationKey: Symbol
    indexName: string
    id: string
}

type FunctionMap = Record<string, Function>

export interface DeepFunctionMap extends Record<string, Function | FunctionMap | DeepFunctionMap> {}

export type ExtensionAPI = DeepFunctionMap
export type ExtensionPublicAPI = Record<string, any>

export type ServiceHandlerType = 'query' | 'action' | 'dataManipulation' | 'queuedDataManipulation'

type ServiceMethod = (...args: any[]) => any

export interface ServiceHandler<THandler extends ServiceMethod = ServiceMethod> {
    handler: THandler
    options: {
        type: ServiceHandlerType
    }
}

export type ServiceAPIHandlers<TServiceAPI> = {
    [K in keyof TServiceAPI as TServiceAPI[K] extends ServiceMethod ? K : never]: ServiceHandler<
        Extract<TServiceAPI[K], ServiceMethod>
    >
}

export interface ServiceAPIEntry<TServiceAPI = Record<string, ServiceMethod>> {
    serviceKey: string
    handlers: ServiceAPIHandlers<TServiceAPI>
}

export type ServiceAPIRegistry = Record<string, Record<string, (...args: any[]) => any>>

export type ExtensionPointers = Record<string, Function | FunctionMap>

// tried: `import { EventEmitter } from 'events'` but it broke this interface
// import {EventEmitter} from 'events'

export type PostTransactionSideEffect = () => Promise<void>

export type PostTransactionOperation = (documentTransaction: Transaction) => void | PostTransactionSideEffect
export type PostSetOperation = (pointer: Pointer, value: any /*DalValue*/) => void

export type FilterGetter = (name: string) => IndexKey

export type Predicate = (value: any, key?: string) => boolean

export type StoreValuePrimitives = object | string | number | boolean
export type StoreValue = StoreValuePrimitives | StoreValuePrimitives[]
export interface DmApis {
    coreConfig: CoreConfig
    dal: DAL
    pointers: Pointers
    extensionAPI: ExtensionAPI
    EVENTS: Record<string, any>
    resourceLoader: ResourceLoaderAPI
}

export type DMApisWithEvents = DmApis & {eventEmitter: EventEmitter}

export type InitializeExtArgs = DMApisWithEvents

export type CreateExtArgs = DMApisWithEvents

export type ResourceLoaderArgs = DMApisWithEvents

export type ValidatorMap = Record<string, ValidateValue>

export interface ValidatorScope {
    include: ('*' | string)[]
    exclude?: string[]
}

export interface Extension {
    readonly name: string
    readonly dependencies?: ReadonlySet<string>
    readonly initialState?: Record<string, any>
    readonly EVENTS?: Record<string, Record<string, string>>
    createExtensionAPI?(args: CreateExtArgs): ExtensionAPI
    createPublicAPI?(args: CreateExtArgs): ExtensionPublicAPI
    createServiceAPI?(args: CreateExtArgs): ServiceAPIEntry
    createPointersMethods?(dmApis: DmApis): PointerMethods
    createGetters?(dmApis: DmApis): Record<string, CustomGetter>
    getDocumentDataTypes?(dmApis: DmApis): DocumentDataTypes
    createFilters?(args: DmApis): Record<string, ValueToIndexIds>
    createValidator?(args: DmApis): ValidatorMap
    createRebaseValidator?(args: DmApis): ValidatorMap
    initialize?(args: InitializeExtArgs): Promise<void>
    initializeAfterDataLoaded?(args: InitializeExtArgs): Promise<void>
    subscribeToEvents?(dmApis: DMApisWithEvents): void
    initializeChannelSubscriptions?(dmApis: DmApis): Promise<void>
    initializeModels?(initialStore: DmStore, initialModelData: ModelsInitializationType, dmApis: DmApis): void
    createResourceLoader?(resourceLoaderArgs: ResourceLoaderArgs): ResourceLoaderDefinition[]
    createPostSetOperation?(dmApis: DmApis): PostSetOperation
    createPostTransactionOperations?(args: CreateExtArgs): Record<string, PostTransactionOperation>
    createTransactionDerivedMetadata?(args: CreateExtArgs): Record<string, [PostTransactionOperation, string?]>
    createChangesListener?(dmApis: DmApis): DalValueChangeCallback
    validatorScope?: ValidatorScope
}

export type PointerMethod = (...args: any[]) => Pointer | Pointer[] | Record<string, any> | boolean | null | undefined
export type PointerMethods = Record<string, Record<string, PointerMethod> | string | PointerMethod>
export interface DocumentDataType {
    hasSignature?: boolean
    idsWithSignature?: Set<string>
}
export type DocumentDataTypes = Record<string, DocumentDataType>

export interface CreateExtensionArgument {
    experimentInstance: Experiment
    dsConfig: DSConfig
    environmentContext: EnvironmentContext
    logger: CoreLogger
}

export type FetchFn = (input: RequestInfo, init?: RequestInit) => Promise<Response>

export interface FetchPagesFacade {
    fetchPages(
        fetchFn: FetchFn,
        pageList: PageListWithMasterAndMainPage | PageList,
        concurrency?: number
    ): Promise<Record<string, any>>
}

interface FragmentResult {
    type: string
    name: string
    content: any
}

export type FragmentFetchFallbackFn = (fragmentType: string, fragmentName: string, jsonFilename: string) => Promise<any>

export interface FetchFragmentsFacade {
    fetchFragments(
        fetchFn: FetchFn,
        fragmentsList: FragmentListItem[],
        urlTemplates: string[],
        concurrency: number,
        fragmentFetchFallbackFn?: FragmentFetchFallbackFn
    ): Promise<FragmentResult[]>
}

//The type of EnvironmentContext should be more explicit and should have some mandatory properties (such as fetchFn, fetchPagesFacade)
// see DM-10455
export type EnvironmentContext = Record<string, any> & {
    loggerDriver?: LoggerDriver
    fetchPagesFacade?: FetchPagesFacade
    fetchFragmentsFacade?: FetchFragmentsFacade
}
export interface ExtensionFactory {
    createExtension(createExtensionArgument: CreateExtensionArgument): Extension
}

export interface LogParams {
    tags?: Record<string, any>
    extras?: Record<string, any>
    context?: Record<string, any>
    shouldSendToPanorama?: boolean
}
export interface CoreLogger extends BaseLogger {
    captureError(error: Error, op?: LogParams): void
    breadcrumb(msg: string, op?: LogParams): void
    flush(): Promise<void>
    reportBI(event: BIEvt, params?: Record<string, any>): void
}

export interface LoggerDriver {
    info(message: string, ...args: any[]): void
    error(message: string, error: Error, ...args: any[]): void
}

export type IdGenerator = (label?: string) => string

export interface WhitelistCheckResult {
    isWhiteListed: boolean
    issue: string
}

export type ValidationWhitelistCheck = (
    pointer: Pointer,
    value: DalValue,
    validatorResult: ValidatorResult
) => WhitelistCheckResult

export interface ValidatorAndWhitelistResult extends ValidatorResult {
    isWhitelisted: boolean
}

export interface BaselineBlacklistCheckResult {
    isBlacklisted: boolean
}
export type BaselineBlacklistCheck = (
    violation: ValidatorAndWhitelistResult,
    pointer: Pointer,
    error: ReportableError
) => BaselineBlacklistCheckResult | void
